import { Backdrop } from "@jugl-web/ui-components/cross-platform/Backdrop";
import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import { PlainButton } from "@jugl-web/ui-components/cross-platform/PlainButton";
import { Tooltip } from "@jugl-web/ui-components/cross-platform/Tooltip";
import { cx, useTranslations } from "@jugl-web/utils";
import React, { useEffect, useRef, useState } from "react";
import { VList, VListHandle } from "virtua";
import { useAIAssistant } from "@jugl-web/domain-resources/ai-assistant/providers";
import { ReactComponent as MagnifyIcon } from "./assets/magnify.svg";
import { ReactComponent as ReduceIcon } from "./assets/reduce.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as RetryIcon } from "./assets/retry.svg";
import { ReactComponent as ReloadIcon } from "./assets/reload.svg";
import { ReactComponent as AttentionIcon } from "./assets/attention.svg";
import arivuImage from "./assets/arivu.png";
import { AIMessageBubble } from "./components/AIMessageBubble";
import { AIChatEmptyState } from "./components/AIChatEmptyState";
import { AIChatInput } from "./components/AIChatInput";
import { AIAssistantLimitAlert } from "../AIAssistantLimitAlert";
import {
  aiGradientBackgroundClassName,
  aiGradientHoverBackgroundClassName,
} from "../consts";

export const AIAssistantDialog: React.FC = () => {
  const { t } = useTranslations();
  const [isFullScreenMode, setIsFullScreenMode] = useState(false);
  const {
    messages,
    agent,
    isAILimitReached,
    isAIAssistantDialogOpen,
    isChannelError,
    isError,
    resendMessage,
    handleOpenAIAssistantDialog,
    reconnectChannel,
  } = useAIAssistant();
  const containerRef = useRef<VListHandle>(null);

  useEffect(() => {
    if (!containerRef.current || !messages.length || !isAIAssistantDialogOpen)
      return;
    containerRef.current.scrollToIndex(messages.length - 1, {
      align: "start",
    });
  }, [messages.length, isAIAssistantDialogOpen]);

  return (
    <>
      {isAIAssistantDialogOpen && isFullScreenMode && <Backdrop />}
      <div
        className={cx(
          "pointer-events-none fixed bottom-5 right-5 z-50 flex h-[940px] max-h-[calc(100%-40px)] w-[560px] max-w-[calc(100%-40px)] flex-col overflow-hidden rounded-[20px] bg-white opacity-0 shadow-[0_24px_56px_rgba(0,0,0,0.32)] transition-opacity",
          isAIAssistantDialogOpen && "pointer-events-auto opacity-100",
          isFullScreenMode &&
            "left-1/2 h-full w-[calc(100%-40px)] max-w-[1400px] -translate-x-1/2"
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-0 border-b border-solid border-[#ECEFF0] px-6 py-[14px]">
          <div className="flex items-center gap-3 overflow-hidden">
            <div
              className={cx(
                "border-grey-200 h-11 w-11 shrink-0 overflow-hidden rounded-full border border-solid",
                aiGradientBackgroundClassName
              )}
            >
              <img
                // NOTE: quick fix to avoid type error
                src={arivuImage as unknown as string}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col truncate">
              <span className="font-secondary w-full truncate text-lg font-medium text-[#383838]">
                Arivu
              </span>
              <span className="font-secondary truncate text-[13px] text-[#828282]">
                {t({
                  id: "ai-assistant-dialog-component.personal-ai-assistant",
                  defaultMessage: "Personal AI Assistant",
                })}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <Tooltip
              placement="bottom"
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  className="hover:bg-grey-100 flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                  onClick={() => setIsFullScreenMode((prev) => !prev)}
                  ref={ref}
                  {...props}
                >
                  {isFullScreenMode ? <ReduceIcon /> : <MagnifyIcon />}
                </PlainButton>
              )}
            >
              {isFullScreenMode
                ? t({
                    id: "ai-assistant-dialog-component.collapse",
                    defaultMessage: "Collapse",
                  })
                : t({
                    id: "ai-assistant-dialog-component.full-view",
                    defaultMessage: "Full View",
                  })}
            </Tooltip>
            <Tooltip
              placement="bottom"
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  className="hover:bg-grey-100 flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                  onClick={() => handleOpenAIAssistantDialog(false)}
                  ref={ref}
                  {...props}
                >
                  <CloseIcon />
                </PlainButton>
              )}
            >
              {t({ id: "common.close", defaultMessage: "Close" })}
            </Tooltip>
          </div>
        </div>
        <div className="flex h-full w-full flex-col gap-6 overflow-y-auto pb-6">
          {isChannelError ? (
            <div className="my-auto flex flex-col items-center">
              <AttentionIcon />
              <span className="text-dark font-secondary mt-4 font-medium leading-4">
                {t({
                  id: "ai-assistant-dialog-component.something-went-wrong",
                  defaultMessage: "Oops! Something went wrong",
                })}
              </span>
              <span className="text-dark-700 font-secondary mt-2.5 text-sm leading-[21px]">
                {t({
                  id: "ai-assistant-dialog-component.loading-data-glitch",
                  defaultMessage:
                    "A little glitch occurred while loading data, please try again",
                })}
              </span>
              <InteractiveContainer
                className={cx(
                  "mt-6 flex h-8 w-[110px] items-center justify-center gap-1.5 rounded-lg",
                  aiGradientBackgroundClassName,
                  aiGradientHoverBackgroundClassName
                )}
                onClick={reconnectChannel}
              >
                <RetryIcon />
                <span className="text-white">
                  {t({ id: "common.retry", defaultMessage: "Retry" })}
                </span>
              </InteractiveContainer>
            </div>
          ) : (
            <>
              <div className="jugl__custom-scrollbar w-full grow overflow-y-auto">
                {messages.length ? (
                  <VList
                    overscan={5}
                    reverse
                    ref={containerRef}
                    className="jugl__custom-scrollbar"
                  >
                    {messages.map((message, index) => (
                      <div
                        className="mx-auto mt-4 max-w-[1200px] px-6"
                        key={message.id}
                      >
                        <AIMessageBubble
                          {...message}
                          isLast={messages.length - 1 === index}
                        />
                      </div>
                    ))}
                    {messages.at(-1)?.isOutgoing &&
                      !isAILimitReached &&
                      !isError && (
                        <div className="mx-auto mt-4 max-w-[1200px] px-6">
                          <AIMessageBubble isLoading />
                        </div>
                      )}
                    {isError && (
                      <div className="mx-auto mt-4 max-w-[1200px] px-6">
                        <div className="flex items-center justify-between gap-4 rounded-[10px] bg-[#FEF8F9] p-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-dark font-secondary font-medium">
                              {t({
                                id: "ai-assistant-dialog-component.processing-error",
                                defaultMessage: "🤖 Oops! Processing error",
                              })}
                            </span>
                            <span className="text-dark-800 text-sm ">
                              {t({
                                id: "ai-assistant-dialog-component.processing-error-try-again",
                                defaultMessage:
                                  "AI Assistant lost in the endless requests, please try again",
                              })}
                            </span>
                          </div>
                          <InteractiveContainer
                            className="bg-tertiary/10 hover:bg-tertiary/20 flex h-8 w-8 items-center justify-center rounded-full transition-colors"
                            onClick={resendMessage}
                          >
                            <ReloadIcon />
                          </InteractiveContainer>
                        </div>
                      </div>
                    )}
                  </VList>
                ) : (
                  <AIChatEmptyState />
                )}
              </div>
              <div className="mx-auto w-full max-w-[1200px] px-6">
                {isAILimitReached ? (
                  <div className="rounded-2xl bg-[linear-gradient(231deg,rgba(239,107,109,0.05)_5.78%,rgba(216,102,26,0.05)_98.46%)]">
                    <AIAssistantLimitAlert orientation="row" />
                  </div>
                ) : (
                  <>
                    <AIChatInput />
                    {agent?.agent !== "help" ? (
                      <div className="text-grey-800 font-secondary mt-2 w-full text-center text-xs italic leading-[140%]">
                        {t({
                          id: "ai-assistant-dialog-component.ai-generated-recent-active-tasks",
                          defaultMessage:
                            "AI-generated output based on data from the 100 most recent active tasks",
                        })}
                      </div>
                    ) : undefined}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
